import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { ShinkansenPayoutService } from '../../../services/shinkansen-payout.service';

@Component({
  selector: 'app-shinkansen-monto-max-modal',
  templateUrl: './shinkansen-monto-max-modal.component.html',
})
export class ShinkansenMontoMaxModalComponent implements OnInit {
  form: FormGroup;
  loading = false;
  loadingActual = true;
  valorActual: number | null = null;
  pagoAutomaticoActivo = false;
  togglingPagoAutomatico = false;

  constructor(
    private shinkansen: ShinkansenPayoutService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ShinkansenMontoMaxModalComponent>,
  ) {
    this.form = this.fb.group({
      valor: new FormControl(null, [
        Validators.required,
        Validators.min(1),
        Validators.pattern(/^[0-9]+$/),
      ]),
    });
  }

  ngOnInit(): void {
    this.shinkansen.getMontoMaxAuto().subscribe({
      next: (res) => {
        this.valorActual = res?.valor ?? null;
        if (this.valorActual !== null) {
          this.form.patchValue({ valor: this.valorActual });
        }
        this.loadingActual = false;
      },
      error: () => {
        this.loadingActual = false;
      },
    });

    this.shinkansen.getPagoAutomatico().subscribe({
      next: (res) => {
        this.pagoAutomaticoActivo = !!res?.activo;
      },
      error: () => {
        this.pagoAutomaticoActivo = false;
      },
    });
  }

  togglePagoAutomatico(): void {
    if (this.togglingPagoAutomatico) return;
    const nuevoEstado = !this.pagoAutomaticoActivo;

    Swal.fire({
      icon: 'question',
      title: nuevoEstado ? '¿Activar pago automático Shinkansen?' : '¿Desactivar pago automático Shinkansen?',
      text: nuevoEstado
        ? 'Las órdenes que cumplan los criterios se pagarán automáticamente.'
        : 'Las órdenes nuevas quedarán para pago manual del operador.',
      showCancelButton: true,
      confirmButtonText: nuevoEstado ? 'Activar' : 'Desactivar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: nuevoEstado ? '#1e5a3f' : '#dc2626',
    }).then((result) => {
      if (!result.isConfirmed) return;

      this.togglingPagoAutomatico = true;
      this.shinkansen.setPagoAutomatico(nuevoEstado).subscribe({
        next: (res) => {
          this.pagoAutomaticoActivo = !!res?.activo;
          this.togglingPagoAutomatico = false;
          Swal.fire({
            icon: 'success',
            title: this.pagoAutomaticoActivo ? 'Pago automático activado' : 'Pago automático desactivado',
            confirmButtonColor: '#1e5a3f',
            timer: 1500,
            showConfirmButton: false,
          });
        },
        error: (err) => {
          this.togglingPagoAutomatico = false;
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: err?.error?.message || err?.message || 'No se pudo cambiar el estado',
            confirmButtonColor: '#1e5a3f',
          });
        },
      });
    });
  }

  formatCLP(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  guardar(): void {
    if (this.form.invalid || this.loading) return;

    const raw = this.form.get('valor')?.value;
    const valor = parseInt(String(raw), 10);
    if (!Number.isInteger(valor) || valor <= 0) {
      Swal.fire({
        icon: 'error',
        title: 'Valor inválido',
        text: 'El monto máximo debe ser un número entero positivo (sin puntos ni decimales).',
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }

    this.loading = true;
    this.shinkansen.updateMontoMaxAuto(valor).subscribe({
      next: (res) => {
        Swal.fire({
          icon: 'success',
          title: 'Monto máximo actualizado',
          html: `<p>Nuevo valor: <strong>${this.formatCLP(res?.valor ?? valor)}</strong></p>`,
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#1e5a3f',
        });
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.loading = false;
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err?.error?.message || err?.message || 'No se pudo actualizar el monto máximo',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#1e5a3f',
        });
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
